<template>
  <div class="product-page">
    <section class="p-hero reveal">
      <SectionBackdrop :bg="bgHero" variant="light" :count="32" />
      <div class="container p-hero-inner">
        <div class="p-hero-copy">
          <div class="p-hero-badge">
            <img :src="logo" alt="FateStar" />
            <span>FateStar</span>
          </div>
          <h1>FateStar</h1>
          <p class="p-hero-lead">
            Traditional calendar and culture tools — daily almanac, BaZi charts, astrology insights,
            and naming helpers grounded in classic Chinese calendrical systems.
          </p>
          <div class="p-hero-features">
            <p v-for="line in featureLines" :key="line" class="p-hero-feature">{{ line }}</p>
          </div>
          <div class="p-hero-actions">
            <a href="/ftp/packages/fatestar/FateStarSetup.exe" download="FateStarSetup.exe" class="btn-download">
              <img src="@/assets/videocvt/Windows.png" alt="Windows" class="btn-os-icon" />
              <span>Free download</span>
            </a>
          </div>
          <p class="p-hero-system">Supported OS: Windows 8 / 10 / 11</p>
          <div class="p-hero-meta">
            <span v-for="item in metaItems" :key="item.label" class="p-hero-meta-item">
              <span class="meta-icon">
                <FeatureIcon :name="item.icon" :alt="item.label" size="sm" />
              </span>
              {{ item.label }}
            </span>
          </div>
        </div>
        <div class="p-hero-visual shot-glow">
          <img :src="heroImage" alt="FateStar preview" class="app-shot" loading="lazy" />
        </div>
      </div>
    </section>

    <section class="p-video reveal">
      <SectionBackdrop :bg="bgFeatures" variant="light" :count="16" />
      <div class="container">
        <div class="p-video-head">
          <p class="overline">Demo</p>
          <h2>Product demo</h2>
          <p>A short walkthrough of the core modules — classic culture tools in a modern desktop app.</p>
        </div>
        <div class="p-video-grid">
          <div class="p-video-wrap">
            <video :src="demoVideo" controls playsinline preload="metadata" :poster="posterImage">
              Your browser does not support video playback.
            </video>
          </div>
          <div class="p-video-copy">
            <h3>Tradition meets desktop utility</h3>
            <p>
              The Book of Changes (I Ching) sits at the root of classic Chinese cosmology —
              yin-yang, Five Elements, and stem-branch calendars. FateStar brings almanac guidance,
              BaZi charts, and astrology tools into one Windows app.
            </p>
            <p>
              Whether you pick auspicious dates, chart BaZi, explore baby naming, or browse zodiac
              outlooks, we aim for clear, practical readouts while respecting cultural context.
            </p>
            <a href="/ftp/packages/fatestar/FateStarSetup.exe" download="FateStarSetup.exe" class="btn-download btn-download-sm">
              <img src="@/assets/videocvt/Windows.png" alt="Windows" class="btn-os-icon" />
              <span>Free download</span>
            </a>
          </div>
        </div>
      </div>
    </section>

    <section class="p-features">
      <SectionBackdrop :bg="bgFeatures" variant="light" :count="20" />
      <div class="container">
        <div class="p-features-head reveal">
          <p class="overline">Features</p>
          <h2>Core features</h2>
          <p>Six modules for everyday culture tools — clean UI, clear explanations, ready to use.</p>
        </div>

        <div class="p-other reveal">
          <div class="p-other-grid p-other-grid--3">
            <article v-for="item in coreFeatures" :key="item.title" class="p-other-card">
              <div class="p-other-icon">
                <FeatureIcon :name="item.icon" :alt="item.title" size="lg" />
              </div>
              <div>
                <h4>{{ item.title }}</h4>
                <p>{{ item.description }}</p>
              </div>
            </article>
          </div>

          <div class="p-culture reveal">
            <article v-for="item in cultureBlocks" :key="item.title" class="p-culture-card">
              <h4>{{ item.title }}</h4>
              <p>{{ item.content }}</p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section class="p-reviews reveal">
      <SectionBackdrop :bg="bgReviews" variant="dark" :count="30" />
      <div class="container">
        <div class="p-reviews-head">
          <h3>What users say</h3>
          <p>Feedback that helps us refine culture tools for everyday use.</p>
        </div>
        <div class="p-reviews-viewport">
          <div class="p-reviews-track" :style="{ transform: `translateX(-${activeReviewGroup * 100}%)` }">
            <div v-for="(group, gi) in reviewGroups" :key="`g-${gi}`" class="p-review-group">
              <article v-for="item in group" :key="item.name" class="p-review-card">
                <div class="p-review-user">
                  <div class="p-review-avatar">
                    <img :src="item.avatar" :alt="item.name" />
                  </div>
                  <div>
                    <div class="p-review-name">{{ item.name }}</div>
                    <div class="p-review-stars" aria-label="5 stars">
                      <span v-for="n in 5" :key="n">★</span>
                    </div>
                  </div>
                </div>
                <p class="p-review-text">{{ item.comment }}</p>
              </article>
            </div>
          </div>
        </div>
        <div class="p-review-dots">
          <button
            v-for="(group, index) in reviewGroups"
            :key="`dot-${index}`"
            class="p-review-dot"
            :class="{ active: activeReviewGroup === index }"
            @click="activeReviewGroup = index"
          ></button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import FeatureIcon from '@/components/FeatureIcon.vue'
import SectionBackdrop from '@/components/SectionBackdrop.vue'

export default {
  name: 'FateStar',
  components: { FeatureIcon, SectionBackdrop },
  data() {
    return {
      observer: null,
      reviewTimer: null,
      activeReviewGroup: 0,
      bgHero: require('@/assets/illustrations/bg-product-hero.svg'),
      bgFeatures: require('@/assets/illustrations/bg-product-features.svg'),
      bgReviews: require('@/assets/illustrations/bg-standards-glow.svg'),
      logo: require('@/assets/fatestar/fatestar_48x48.png'),
      heroImage: require('@/assets/fatestar/首页.png'),
      demoVideo: require('@/assets/fatestar/星运八字.mp4'),
      posterImage: require('@/assets/fatestar/首页.png'),
      featureLines: [
        'Almanac / astrology / BaZi chart / baby naming',
        'Compatibility / zodiac outlook / solar terms / cultural notes'
      ],
      metaItems: [
        { icon: 'yinyang', label: 'Classic systems' },
        { icon: 'calendar', label: 'Daily almanac' },
        { icon: 'sparkle', label: 'Clear readings' }
      ],
      coreFeatures: [
        { title: 'Almanac', description: 'Daily do/avoid guidance, solar terms, and directional notes at a glance.', icon: 'calendar' },
        { title: 'Astrology', description: 'Traits and outlook based on birth date for quick reference.', icon: 'star' },
        { title: 'BaZi chart', description: 'Auto chart from birth time — pillars, ten gods, luck cycles, and more.', icon: 'yinyang' },
        { title: 'Baby naming', description: 'Name ideas informed by Five Elements preferences and sound meaning.', icon: 'tag' },
        { title: 'Compatibility', description: 'Zodiac, astrology, and BaZi dimensions with practical notes.', icon: 'heart' },
        { title: 'Zodiac outlook', description: 'Yearly, monthly, and daily outlooks by Chinese zodiac.', icon: 'sparkle' }
      ],
      cultureBlocks: [
        {
          title: 'Know the pattern, act with agency',
          content: 'Classic texts emphasize self-strengthening. Charting is a lens on timing and temperament — not fatalism. Understanding strengths helps you act with clearer intent.'
        },
        {
          title: 'Choose auspicious moments',
          content: 'Almanac culture records daily guidance for weddings, moves, openings, and travel. Picking a date can add calm and ritual to milestones that matter.'
        },
        {
          title: 'Names that carry meaning',
          content: 'Naming is a lasting cultural practice. A good name balances sound, meaning, and elemental harmony — a gift that stays with a child for life.'
        }
      ],
      reviews: [
        { name: 'Nina', comment: 'Daily almanac is handy — a quick check before big plans.', avatar: require('@/assets/fatestar/avatar1.jpg') },
        { name: 'Omar', comment: 'BaZi charts are clear; luck cycles are easier than scattered web tools.', avatar: require('@/assets/fatestar/avatar2.jpg') },
        { name: 'Priya', comment: 'Astrology and zodiac in one app — no more tab hopping.', avatar: require('@/assets/fatestar/avatar3.jpeg') },
        { name: 'Reed', comment: 'Naming suggestions with elemental notes made our family discussion easier.', avatar: require('@/assets/fatestar/avatar4.jpeg') },
        { name: 'Sage', comment: 'Compatibility checks are a fun conversation starter with my partner.', avatar: require('@/assets/fatestar/avatar5.jpg') },
        { name: 'Tess', comment: 'Culture notes are readable — I use the tools and learn a bit along the way.', avatar: require('@/assets/fatestar/avatar6.jpeg') }
      ]
    }
  },
  computed: {
    reviewGroups() {
      return [this.reviews.slice(0, 3), this.reviews.slice(3, 6)]
    }
  },
  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            this.observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12 }
    )
    this.$el.querySelectorAll('.reveal').forEach(el => this.observer.observe(el))
    this.reviewTimer = setInterval(() => {
      this.activeReviewGroup = this.activeReviewGroup === 0 ? 1 : 0
    }, 4200)
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
    if (this.reviewTimer) clearInterval(this.reviewTimer)
  }
}
</script>
